// Mixeren bag al lyd. Tre busser (musik, effekter, UI) løber gennem master, en compressor og
// en limiter, så mange samtidige lyde aldrig klipper. Højst 12 stemmer: når der er fuldt, stjæles
// den ældste og svageste. AudioContext oprettes først ved første tryk (browserkrav).

export type Bus = 'music' | 'sfx' | 'ui';

export interface Volumes {
  master: number;
  music: number;
  sfx: number;
}

interface Voice {
  stop: (t: number) => void;
  end: number;
  vol: number;
  start: number;
}

const MAX_VOICES = 12;

export class Mixer {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buses = new Map<Bus, GainNode>();
  private duckGain: GainNode | null = null;
  private voices: Voice[] = [];
  private noiseBuf: AudioBuffer | null = null;
  private vol: Volumes;
  private muted: boolean;
  private listeners: Array<() => void> = [];

  constructor(vol: Volumes, muted: boolean) {
    this.vol = { ...vol };
    this.muted = muted;
    const unlock = (): void => {
      this.unlock();
      if (!this.ctx) return;
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }

  /** Kaldes, når lyden er låst op, så musikken kan starte. */
  onUnlock(fn: () => void): void {
    if (this.ready) fn();
    else this.listeners.push(fn);
  }

  get ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  /** Lyden spiller kun, når den er låst op og ikke slukket. */
  get live(): boolean {
    return this.ready && !this.muted;
  }

  get now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  private unlock(): void {
    if (this.ctx) {
      if (this.ctx.state !== 'running') void this.ctx.resume();
      return;
    }
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      this.ctx = ctx;
      // Compressor tager toppene, en hård limiter lige efter fanger resten.
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.knee.value = 12;
      comp.ratio.value = 4;
      comp.attack.value = 0.004;
      comp.release.value = 0.2;
      const lim = ctx.createDynamicsCompressor();
      lim.threshold.value = -2;
      lim.knee.value = 0;
      lim.ratio.value = 20;
      lim.attack.value = 0.001;
      lim.release.value = 0.08;
      this.master = ctx.createGain();
      this.master.connect(comp).connect(lim).connect(ctx.destination);
      this.duckGain = ctx.createGain();
      this.duckGain.connect(this.master);
      for (const b of ['music', 'sfx', 'ui'] as Bus[]) {
        const g = ctx.createGain();
        g.connect(b === 'music' ? this.duckGain : this.master);
        this.buses.set(b, g);
      }
      this.apply();
      const fire = (): void => {
        if (ctx.state !== 'running') return;
        const ls = this.listeners;
        this.listeners = [];
        for (const fn of ls) fn();
      };
      ctx.onstatechange = fire;
      void ctx.resume().then(fire, () => undefined);
      fire();
    } catch {
      this.ctx = null;
    }
  }

  private apply(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.vol.master * 0.7, t, 0.03);
    this.buses.get('music')?.gain.setTargetAtTime(this.vol.music * 0.55, t, 0.05);
    this.buses.get('sfx')?.gain.setTargetAtTime(this.vol.sfx, t, 0.03);
    this.buses.get('ui')?.gain.setTargetAtTime(this.vol.sfx * 0.8, t, 0.03);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.apply();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setVolumes(v: Volumes): void {
    this.vol = { ...v };
    this.apply();
  }

  /** Dæmp musikken et øjeblik, så en vigtig effekt kan høres. */
  duck(amount = 0.4, ms = 600): void {
    const ctx = this.ctx;
    const g = this.duckGain;
    if (!ctx || !g) return;
    const t = ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setTargetAtTime(amount, t, 0.02);
    g.gain.setTargetAtTime(1, t + ms / 1000, 0.25);
  }

  bus(b: Bus): GainNode | null {
    return this.buses.get(b) ?? null;
  }

  /** Reservér en stemme. Ved fuldt hus stoppes den ældste, svageste først. */
  private claim(v: Voice): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    this.voices = this.voices.filter((x) => x.end > t);
    if (this.voices.length >= MAX_VOICES) {
      let worst = 0;
      let score = Infinity;
      this.voices.forEach((x, i) => {
        const s = x.vol * 4 - (t - x.start);
        if (s < score) {
          score = s;
          worst = i;
        }
      });
      const [gone] = this.voices.splice(worst, 1);
      gone?.stop(t);
    }
    this.voices.push(v);
  }

  /** En tone med glide og blød kuvert. */
  tone(bus: Bus, t: number, type: OscillatorType, f0: number, f1: number, dur: number, vol: number, attack = 0.01): void {
    const ctx = this.ctx;
    const out = this.buses.get(bus);
    if (!ctx || !out || this.muted) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + dur + 0.05);
    this.claim({
      start: t,
      end: t + dur + 0.05,
      vol,
      stop: (now) => {
        try {
          g.gain.cancelScheduledValues(now);
          g.gain.setTargetAtTime(0.0001, now, 0.01);
          o.stop(now + 0.05);
        } catch {
          // Allerede stoppet.
        }
      },
    });
  }

  /** Filtreret støj: rumlen, sus, gnister og trommer. */
  noise(bus: Bus, t: number, dur: number, freq: number, vol: number, kind: BiquadFilterType = 'lowpass', attack = 0.01, q = 0.8): void {
    const ctx = this.ctx;
    const out = this.buses.get(bus);
    if (!ctx || !out || this.muted) return;
    if (!this.noiseBuf) {
      const len = ctx.sampleRate * 2;
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      // Fast pseudotilfældig støj: lyden er kosmetik, men ens hver gang er rart at teste med.
      let x = 0x2545f491;
      for (let i = 0; i < len; i++) {
        x ^= x << 13;
        x ^= x >>> 17;
        x ^= x << 5;
        d[i] = ((x >>> 0) / 4294967296) * 2 - 1;
      }
      this.noiseBuf = buf;
    }
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = kind;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(out);
    const offset = (t * 7.31) % 1.5;
    src.start(t, offset);
    src.stop(t + dur + 0.05);
    this.claim({
      start: t,
      end: t + dur + 0.05,
      vol,
      stop: (now) => {
        try {
          g.gain.cancelScheduledValues(now);
          g.gain.setTargetAtTime(0.0001, now, 0.01);
          src.stop(now + 0.05);
        } catch {
          // Allerede stoppet.
        }
      },
    });
  }
}
